import { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bell,
  ClipboardText,
  VideoCamera,
  ChatCircle,
  Clock,
  CheckCircle,
  ArrowRight,
  Calendar,
  User,
  Heart,
  Envelope,
  Sparkle,
  CurrencyDollar,
  HandHeart,
  Hourglass,
  Phone,
  EyeSlash,
  Eye,
  ShieldWarning,
  Prohibit,
  Warning
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [privacyMode, setPrivacyMode] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportingMatch, setReportingMatch] = useState(null);
  const [reportForm, setReportForm] = useState({
    reason: '',
    details: '',
    report_type: 'inappropriate_behavior'
  });

  const fetchData = useCallback(async () => {
    try {
      const [notifRes, matchesRes, privacyRes] = await Promise.all([
        axios.get(`${API}/notifications`),
        axios.get(`${API}/matches/my`),
        axios.get(`${API}/user/privacy-mode`)
      ]);
      setNotifications(notifRes.data);
      setMatches(matchesRes.data);
      setPrivacyMode(privacyRes.data.privacy_mode);
    } catch (error) {
      // Dashboard fetch failed — user will see empty state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      // Mark-as-read failed silently — non-critical UX
    }
  };

  const togglePrivacyMode = async () => {
    try {
      const newMode = !privacyMode;
      await axios.put(`${API}/user/privacy-mode`, { privacy_mode: newMode });
      setPrivacyMode(newMode);
      toast.success(newMode ? 'Privacy mode enabled - your profile is now hidden' : 'Privacy mode disabled - you\'re visible to matches again');
    } catch (error) {
      toast.error('Failed to update privacy mode');
    }
  };

  const blockUser = async (matchUserId) => {
    if (!window.confirm('Are you sure you want to block this user? They will not be matched with you again.')) return;
    try {
      await axios.post(`${API}/user/block`, { blocked_user_id: matchUserId });
      toast.success('User blocked');
      fetchData();
    } catch (error) {
      toast.error('Failed to block user');
    }
  };

  const openReportDialog = (match) => {
    setReportingMatch(match);
    setShowReportDialog(true);
  };

  const submitReport = async () => {
    if (!reportForm.reason.trim()) {
      toast.error('Please provide a reason for the report');
      return;
    }
    try {
      await axios.post(`${API}/user/report`, {
        reported_user_id: reportingMatch.match_user?.id || reportingMatch.user_a_id,
        ...reportForm
      });
      toast.success('Report submitted. Our team will review it within 24-72 hours.');
      setShowReportDialog(false);
      setReportForm({ reason: '', details: '', report_type: 'inappropriate_behavior' });
      setReportingMatch(null);
    } catch (error) {
      toast.error('Failed to submit report');
    }
  };

  // Get current stage from user data
  const currentStage = user?.journey_stage || user?.application_status || 'not_submitted';

  // Stage information with descriptions
  const stages = [
    { 
      key: 'not_submitted', 
      label: 'Apply', 
      icon: ClipboardText,
      description: 'Submit your application to start your journey',
      action: { label: 'Complete Application', link: '/application' }
    },
    { 
      key: 'applied', 
      label: 'Review', 
      icon: Hourglass,
      description: 'Your application is being reviewed by our team',
      action: null
    },
    { 
      key: 'accepted', 
      label: 'Accepted', 
      icon: CheckCircle,
      description: 'Congratulations! You have been accepted. We will contact you to schedule your consultation.',
      action: null
    },
    { 
      key: 'consultation_scheduled', 
      label: 'Consultation', 
      icon: Phone,
      description: 'Your personal consultation is scheduled. We will call you at the designated time.',
      action: null
    },
    { 
      key: 'consultation_done', 
      label: 'Select Plan', 
      icon: CurrencyDollar,
      description: 'Choose a matchmaking plan that works best for you. We will reach out with payment options.',
      action: null
    },
    { 
      key: 'paid', 
      label: 'Profile', 
      icon: User,
      description: 'Complete your deeper profile questions so we can find your perfect match.',
      action: null
    },
    { 
      key: 'profile_complete', 
      label: 'Matching', 
      icon: Heart,
      description: 'Your profile is complete! Our matchmakers are searching for compatible matches.',
      action: null
    },
    { 
      key: 'matching', 
      label: 'Active', 
      icon: Sparkle,
      description: 'We are actively reviewing potential matches for you.',
      action: null
    },
    { 
      key: 'matched', 
      label: 'Matched', 
      icon: Heart,
      description: 'You have been matched! Check your matches below.',
      action: null
    }
  ];

  // Get current stage index
  const currentStageIndex = stages.findIndex(s => s.key === currentStage || 
    (currentStage === 'submitted' && s.key === 'applied'));

  const getMatchStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: 'Pending', class: 'status-pending' },
      'scheduling': { label: 'Select Time', class: 'status-pending' },
      'scheduled': { label: 'Call Scheduled', class: 'status-scheduled' },
      'completed': { label: 'Completed', class: 'status-completed' }
    };
    const config = statusMap[status] || { label: status, class: 'status-pending' };
    return <span className={`status-badge ${config.class}`}>{config.label}</span>;
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const currentStageInfo = stages[currentStageIndex] || stages[0];

  return (
    <PageLayout>
      <div className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-serif text-3xl md:text-4xl mb-2" data-testid="dashboard-title">
                Welcome, {user?.name}
              </h1>
              <p className="text-muted-foreground">
                Your matchmaking journey with Link, Laugh & Love
              </p>
            </div>
          </div>

          {/* Privacy Mode Banner */}
          {privacyMode && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center gap-4" data-testid="privacy-mode-banner">
              <EyeSlash weight="fill" className="w-6 h-6 text-amber-600" />
              <div className="flex-1">
                <p className="text-amber-800 font-medium">Privacy Mode is ON</p>
                <p className="text-amber-600 text-sm">Your profile is hidden from potential matches</p>
              </div>
              <Button variant="outline" size="sm" onClick={togglePrivacyMode} className="border-amber-300 text-amber-700 hover:bg-amber-100">
                Turn Off
              </Button>
            </div>
          )}

          {/* Privacy Mode Card */}
          <div className="paper-card rounded-lg p-6 mb-8" data-testid="privacy-settings">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${privacyMode ? 'bg-amber-100' : 'bg-green-100'}`}>
                  {privacyMode ? (
                    <EyeSlash weight="fill" className="w-6 h-6 text-amber-600" />
                  ) : (
                    <Eye weight="fill" className="w-6 h-6 text-green-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-medium">Privacy Mode (Incognito)</h3>
                  <p className="text-muted-foreground text-sm">
                    {privacyMode 
                      ? 'Your profile is hidden. Turn off when you\'re ready to be matched again.'
                      : 'Your profile is visible to our matchmakers. Turn on to take a break.'}
                  </p>
                </div>
              </div>
              <Switch
                checked={privacyMode}
                onCheckedChange={togglePrivacyMode}
                data-testid="privacy-mode-toggle"
              />
            </div>
          </div>

          {/* Journey Progress */}
          <div className="paper-card rounded-lg p-6 md:p-8 mb-8" data-testid="journey-progress">
            <h2 className="font-serif text-xl mb-6 flex items-center gap-2">
              <Sparkle weight="fill" className="w-5 h-5 text-primary" />
              Your Journey
            </h2>

            {/* Progress Steps - Simplified for key milestones */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                {stages.slice(0, 7).map((stage, i) => {
                  const isCompleted = i < currentStageIndex;
                  const isCurrent = i === currentStageIndex;
                  const Icon = stage.icon;
                  
                  return (
                    <div key={stage.key} className="flex flex-col items-center flex-1">
                      <div
                        className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-sm transition-all ${
                          isCompleted
                            ? 'bg-green-500 text-white'
                            : isCurrent
                            ? 'bg-primary text-white shadow-lg ring-4 ring-primary/20'
                            : 'bg-pink-100 text-pink-400'
                        }`}
                      >
                        {isCompleted ? (
                          <CheckCircle weight="fill" className="w-5 h-5" />
                        ) : (
                          <Icon weight={isCurrent ? 'fill' : 'regular'} className="w-5 h-5" />
                        )}
                      </div>
                      <span className={`text-xs mt-2 text-center hidden md:block ${
                        isCurrent ? 'text-primary font-semibold' : 'text-muted-foreground'
                      }`}>
                        {stage.label}
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* Progress bar */}
              <div className="h-2 bg-pink-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-primary to-pink-400 rounded-full transition-all duration-700"
                  style={{ width: `${Math.min((currentStageIndex / 6) * 100, 100)}%` }}
                />
              </div>
            </div>

            {/* Current Stage Info Card */}
            <div className="bg-gradient-to-r from-primary/10 to-pink-50 rounded-lg p-6 border-l-4 border-primary">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <currentStageInfo.icon weight="fill" className="w-7 h-7 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-serif text-lg mb-1">
                    Current Status: <span className="text-primary">{currentStageInfo.label}</span>
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {currentStageInfo.description}
                  </p>
                  {currentStageInfo.action && (
                    <Link to={currentStageInfo.action.link}>
                      <Button className="btn-primary" data-testid="stage-action-btn">
                        {currentStageInfo.action.label}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  )}
                  {user?.consultation_date && currentStage === 'consultation_scheduled' && (
                    <div className="flex items-center gap-2 text-primary mt-2">
                      <Calendar className="w-4 h-4" />
                      <span className="font-medium">
                        Scheduled: {new Date(user.consultation_date).toLocaleDateString()} at {new Date(user.consultation_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    </div>
                  )}
                  {user?.plan && (currentStage === 'paid' || currentStage === 'profile_complete' || currentStage === 'matching' || currentStage === 'matched') && (
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className="bg-primary/20 text-primary border-0">
                        {user.plan.charAt(0).toUpperCase() + user.plan.slice(1)} Plan
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* What to Expect Section */}
              {(currentStage === 'applied' || currentStage === 'submitted') && (
                <div className="paper-card rounded-lg p-6">
                  <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
                    <HandHeart weight="light" className="w-6 h-6 text-primary" />
                    What Happens Next?
                  </h2>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle weight="fill" className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <span className="font-medium">Application Review</span>
                        <p className="text-muted-foreground text-sm">Our team reviews your application within 72 hours</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full border-2 border-pink-200 mt-0.5" />
                      <div>
                        <span className="font-medium">Personal Consultation</span>
                        <p className="text-muted-foreground text-sm">A 20-30 minute call to discuss your goals</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full border-2 border-pink-200 mt-0.5" />
                      <div>
                        <span className="font-medium">Plan Selection</span>
                        <p className="text-muted-foreground text-sm">Choose the matchmaking package that fits you</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full border-2 border-pink-200 mt-0.5" />
                      <div>
                        <span className="font-medium">Start Matching!</span>
                        <p className="text-muted-foreground text-sm">We begin finding your perfect match</p>
                      </div>
                    </li>
                  </ul>
                </div>
              )}

              {/* Matches */}
              <div>
                <h2 className="font-serif text-2xl mb-6 flex items-center gap-2">
                  <Heart weight="light" className="w-6 h-6 text-primary" />
                  Your Matches
                </h2>

                {loading ? (
                  <div className="paper-card rounded-lg p-8 text-center text-muted-foreground">
                    Loading...
                  </div>
                ) : matches.length === 0 ? (
                  <div className="paper-card rounded-lg p-8 text-center">
                    <Heart weight="thin" className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                    <h3 className="font-serif text-xl mb-2">No Matches Yet</h3>
                    <p className="text-muted-foreground">
                      {currentStageIndex < 6
                        ? 'Complete the steps in your journey to start receiving matches.'
                        : 'Our matchmakers are searching for your perfect match. Sit tight!'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {matches.map((match) => (
                      <div 
                        key={match.id} 
                        className="paper-card rounded-lg p-6"
                        data-testid={`match-card-${match.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                              <User weight="light" className="w-7 h-7 text-primary" />
                            </div>
                            <div>
                              <h3 className="font-serif text-lg">
                                {match.match_user?.name || 'Your Match'}
                              </h3>
                              <p className="text-muted-foreground text-sm">
                                {match.match_user?.location}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {getMatchStatusBadge(match.status)}
                          </div>
                        </div>

                        {/* Match Actions based on status */}
                        <div className="mt-4 pt-4 border-t border-border">
                          <div className="flex flex-wrap items-center gap-3">
                            {match.status === 'scheduling' && (
                              <Link to={`/video-call/${match.id}`}>
                                <Button className="btn-secondary" data-testid={`select-time-btn-${match.id}`}>
                                  <Calendar className="w-4 h-4 mr-2" />
                                  Select Time Slot
                                </Button>
                              </Link>
                            )}
                            {match.status === 'scheduled' && (
                              <>
                                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                                  <Clock className="w-4 h-4" />
                                  <span>Scheduled: {new Date(match.scheduled_time).toLocaleString()}</span>
                                </div>
                                <Link to={`/video-call/${match.id}`}>
                                  <Button className="btn-primary" data-testid={`join-call-btn-${match.id}`}>
                                    <VideoCamera className="w-4 h-4 mr-2" />
                                    Join Video Call
                                  </Button>
                                </Link>
                              </>
                            )}
                            {match.status === 'completed' && !match.feedback_submitted && (
                              <Link to={`/feedback/${match.id}`}>
                                <Button className="btn-secondary" data-testid={`submit-feedback-btn-${match.id}`}>
                                  <ChatCircle className="w-4 h-4 mr-2" />
                                  Submit Feedback
                                </Button>
                              </Link>
                            )}
                            {match.contact_exchanged && (
                              <div className="flex items-center gap-2 text-accent">
                                <Envelope className="w-4 h-4" />
                                <span>Contact shared: {match.match_contact?.email}</span>
                              </div>
                            )}

                            {/* Block & Report Actions */}
                            <div className="ml-auto flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openReportDialog(match)}
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                data-testid={`report-match-btn-${match.id}`}
                              >
                                <ShieldWarning className="w-4 h-4 mr-1" />
                                Report
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => blockUser(match.match_user?.id)}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                data-testid={`block-match-btn-${match.id}`}
                              >
                                <Prohibit className="w-4 h-4 mr-1" />
                                Block
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Notifications Sidebar */}
            <div>
              <h2 className="font-serif text-2xl mb-6 flex items-center gap-2">
                <Bell weight="light" className="w-6 h-6 text-primary" />
                Notifications
                {unreadCount > 0 && (
                  <Badge variant="destructive" className="ml-2">{unreadCount}</Badge>
                )}
              </h2>

              <div className="space-y-3">
                {notifications.length === 0 ? (
                  <div className="paper-card rounded-lg p-6 text-center text-muted-foreground">
                    No notifications yet
                  </div>
                ) : (
                  notifications.slice(0, 10).map((notif) => (
                    <div 
                      key={notif.id}
                      className={`paper-card rounded-lg p-4 cursor-pointer transition-all ${
                        !notif.read ? 'border-l-4 border-l-primary' : ''
                      }`}
                      onClick={() => markAsRead(notif.id)}
                      data-testid={`notification-${notif.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                          notif.read ? 'bg-muted' : 'bg-primary'
                        }`} />
                        <div>
                          <h4 className="font-medium text-sm">{notif.title}</h4>
                          <p className="text-muted-foreground text-xs mt-1">
                            {notif.message}
                          </p>
                          <span className="text-xs text-muted-foreground mt-2 block">
                            {new Date(notif.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Contact Info Card */}
              <div className="paper-card rounded-lg p-6 mt-6 bg-pink-50">
                <h3 className="font-serif text-lg mb-3">Questions?</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  We are here to help you on your journey to find love.
                </p>
                <div className="space-y-2 text-sm">
                  <a href="mailto:LinkLaughLoveMatch@gmail.com" className="flex items-center gap-2 text-primary hover:underline">
                    <Envelope className="w-4 h-4" />
                    LinkLaughLoveMatch@gmail.com
                  </a>
                  <a href="tel:8107991060" className="flex items-center gap-2 text-primary hover:underline">
                    <Phone className="w-4 h-4" />
                    (810) 799-1060
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <ShieldWarning className="w-6 h-6 text-amber-500" />
              Report a Concern
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-muted-foreground text-sm">
              We take all reports seriously. Our team will review your concern within 24-72 hours. 
              Your report is confidential and will not be shared with the other person.
            </p>

            <div>
              <label className="text-sm font-medium mb-1 block">Type of Concern</label>
              <Select 
                value={reportForm.report_type} 
                onValueChange={(v) => setReportForm(prev => ({ ...prev, report_type: v }))}
              >
                <SelectTrigger className="form-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inappropriate_behavior">Inappropriate Behavior</SelectItem>
                  <SelectItem value="harassment">Harassment or Abuse</SelectItem>
                  <SelectItem value="fake_profile">Fake or Misleading Profile</SelectItem>
                  <SelectItem value="safety_concern">Safety Concern</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">What happened? *</label>
              <Textarea
                placeholder="Please describe your concern..."
                value={reportForm.reason}
                onChange={(e) => setReportForm(prev => ({ ...prev, reason: e.target.value }))}
                className="form-input"
                rows={3}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Additional Details (optional)</label>
              <Textarea
                placeholder="Any other information that might help us..."
                value={reportForm.details}
                onChange={(e) => setReportForm(prev => ({ ...prev, details: e.target.value }))}
                className="form-input"
                rows={2}
              />
            </div>

            <div className="bg-pink-50 rounded-lg p-4 text-sm">
              <p className="text-muted-foreground">
                <strong>Need immediate help?</strong> If you feel unsafe, please contact local authorities. 
                For urgent concerns, call us at <a href="tel:8107991060" className="text-primary">(810) 799-1060</a>.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="ghost" 
              onClick={() => { setShowReportDialog(false); setReportForm({ reason: '', details: '', report_type: 'inappropriate_behavior' }); }}
            >
              Cancel
            </Button>
            <Button onClick={submitReport} className="btn-primary" disabled={!reportForm.reason.trim()}>
              <ShieldWarning className="w-4 h-4 mr-2" />
              Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default Dashboard;
